import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useData } from '../../context/DataContext';
import Tooltip from '../common/Tooltip';
import FundPriceModal from '../FundPrices/FundPriceModal';
import { formatCurrency } from '../../utils/formatters';

const AccountForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { portfolios, goals, updateGoal, addPortfolio, updatePortfolio, updatePortfolioValue, addTransaction, fundPrices } = useData();
  const allPots = [];
  const [valueMode, setValueMode] = useState('simple');
  const [formData, setFormData] = useState({
    name: '',
    type: 'ISA',
    currency: 'GBP',
    currentValue: 0,
    costBasis: 0,
    monthlyContribution: 0,
    contributionDay: '',
    contributionSplits: [],
    lowReturn: 3,
    averageReturn: 7,
    highReturn: 10,
  });
  const [pots, setPots] = useState([]);
  const [errors, setErrors] = useState({});
  const [isFundModalVisible, setIsFundModalVisible] = useState(false);
  const [potIdForFundSelection, setPotIdForFundSelection] = useState(null);

  const account = id ? portfolios.find(p => p.id === id) : null;

  const accountTypes = [
    { id: 'ISA', name: 'ISA' },
    { id: 'SIPP', name: 'SIPP' },
    { id: 'GIA', name: 'General Investment Account (GIA)' },
    { id: 'SAVINGS', name: 'Savings Account' },
    { id: 'PENSION', name: 'Pension' },
    { id: 'OTHER', name: 'Other' },
  ];

  const currencies = [
    { id: 'GBP', name: 'GBP (British Pound)' },
    { id: 'USD', name: 'USD (US Dollar)' },
    { id: 'EUR', name: 'EUR (Euro)' },
    { id: 'JPY', name: 'JPY (Japanese Yen)' },
  ];

  useEffect(() => {
    if (account) {
      setFormData({
        name: account.name || '',
        type: account.type || 'ISA',
        currency: account.currency || 'GBP',
        currentValue: account.currentValue || 0,
        costBasis: account.costBasis || 0,
        monthlyContribution: account.monthlyContribution || 0,
        contributionDay: account.contributionDay || '',
        contributionSplits: account.contributionSplits || [],
        lowReturn: account.lowReturn || 3,
        averageReturn: account.averageReturn || account.estimatedReturnRate || 7,
        highReturn: account.highReturn || account.optimisticReturn || (account.estimatedReturnRate ? account.estimatedReturnRate + 3 : 10),
      });
      setValueMode('simple');
      setPots([]);
    }
  }, [account]);

  const addNewPot = () => {
    setPots([...pots, {
      id: `temp-pot-${Date.now()}`,
      type: 'cash',
      name: '',
      amount: 0,
      fundId: '',
      symbol: '',
      units: 0,
      pricePerUnit: 0,
      costBasis: 0,
      currency: formData.currency,
      lowReturn: 3,
      averageReturn: 7,
      highReturn: 10,
    }]);
  };

  const handleOpenFundModal = (potId) => {
    setPotIdForFundSelection(potId);
    setIsFundModalVisible(true);
  };

  const handleCloseFundModal = () => {
    setIsFundModalVisible(false);
    setPotIdForFundSelection(null);
  };

  const handleFundAdded = (newFund) => {
    // Auto-select the newly added fund for the pot
    if (potIdForFundSelection && newFund) {
      setPots(prevPots => prevPots.map(p => {
        if (p.id === potIdForFundSelection) {
          return {
            ...p,
            fundId: newFund.id,
            name: p.name.trim() === '' ? newFund.name : p.name,
            symbol: newFund.symbol || '',
            pricePerUnit: newFund.price || 0,
          };
        }
        return p;
      }));
    }
  };

  const removePot = (potId) => {
    setPots(pots.filter(p => p.id !== potId));
  };

  const updatePotField = (potId, field, value) => {
    setPots(pots.map(p => {
      if (p.id === potId) {
        const updated = { ...p, [field]: value };

        // If fundId changed, auto-fill pot name, symbol, and price
        if (field === 'fundId' && value) {
          const selectedFund = fundPrices.find(f => f.id === value);
          if (selectedFund) {
            updated.name = p.name.trim() === '' ? selectedFund.name : p.name;
            updated.symbol = selectedFund.symbol || '';
            updated.pricePerUnit = selectedFund.price || 0;
          }
        }

        return updated;
      }
      return p;
    }));
  };

  const calculateTotalFromPots = () => {
    return pots.reduce((sum, pot) => {
      if (pot.type === 'cash') {
        return sum + (parseFloat(pot.amount) || 0);
      } else {
        return sum + ((parseFloat(pot.units) || 0) * (parseFloat(pot.pricePerUnit) || 0));
      }
    }, 0);
  };

  const calculateCostBasisFromPots = () => {
    return pots.reduce((sum, pot) => {
      if (pot.type === 'cash') {
        return sum + (parseFloat(pot.amount) || 0);
      } else {
        return sum + (parseFloat(pot.costBasis) || 0);
      }
    }, 0);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Please enter account name';
    }
    if (!formData.type) {
      newErrors.type = 'Please select account type';
    }
    if (!formData.currency) {
      newErrors.currency = 'Please select currency';
    }

    if (valueMode === 'simple') {
      if (formData.currentValue === null || formData.currentValue === undefined || formData.currentValue < 0) {
        newErrors.currentValue = 'Please enter current value';
      }
      if (formData.costBasis === null || formData.costBasis === undefined || formData.costBasis < 0) {
        newErrors.costBasis = 'Please enter cost basis';
      }
    }

    if (valueMode === 'detailed') {
      if (pots.length === 0) {
        newErrors.pots = 'Please add at least one pot or switch to simple mode';
      } else {
        pots.forEach((pot, index) => {
          if (!pot.name || pot.name.trim() === '') {
            newErrors[`pot_${pot.id}_name`] = `Pot ${index + 1}: Please enter name`;
          }
          if (pot.type === 'cash') {
            if (pot.amount === null || pot.amount === undefined || pot.amount <= 0) {
              newErrors[`pot_${pot.id}_amount`] = `Pot ${index + 1}: Please enter amount`;
            }
          } else if (pot.type === 'stock') {
            if (pot.units === null || pot.units === undefined || pot.units <= 0) {
              newErrors[`pot_${pot.id}_units`] = `Pot ${index + 1}: Please enter units`;
            }
            if (pot.pricePerUnit === null || pot.pricePerUnit === undefined || pot.pricePerUnit <= 0) {
              newErrors[`pot_${pot.id}_price`] = `Pot ${index + 1}: Please enter price`;
            }
            if (pot.costBasis === null || pot.costBasis === undefined || pot.costBasis <= 0) {
              newErrors[`pot_${pot.id}_costBasis`] = `Pot ${index + 1}: Please enter cost basis`;
            }
          }
        });
      }
    }

    if (valueMode === 'simple') {
      if (formData.lowReturn > formData.averageReturn) {
        newErrors.lowReturn = 'Low return must be less than or equal to average return';
      }
      if (formData.averageReturn > formData.highReturn) {
        newErrors.averageReturn = 'Average return must be less than or equal to high return';
      }
      if (formData.lowReturn > formData.highReturn) {
        newErrors.highReturn = 'High return must be greater than or equal to low return';
      }
    }

    if (valueMode === 'detailed') {
      pots.forEach((pot, index) => {
        if (pot.lowReturn > pot.averageReturn) {
          newErrors[`pot_${pot.id}_lowReturn`] = `Pot ${index + 1}: Low return must be ≤ average`;
        }
        if (pot.averageReturn > pot.highReturn) {
          newErrors[`pot_${pot.id}_avgReturn`] = `Pot ${index + 1}: Average return must be ≤ high`;
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (keepOpen = false) => {
    if (!validate()) {
      return;
    }

    if (account) {
      const newValue = parseFloat(formData.currentValue) || 0;
      const newCostBasis = parseFloat(formData.costBasis) || 0;
      const valueChanged = newValue !== account.currentValue;
      const costBasisChanged = newCostBasis !== account.costBasis;

      if (valueChanged || costBasisChanged) {
        updatePortfolioValue(account.id, newValue, costBasisChanged ? newCostBasis : null);
        const { currentValue, costBasis, ...otherUpdates } = formData;
        updatePortfolio(account.id, otherUpdates);
      } else {
        updatePortfolio(account.id, formData);
      }
      navigate('/accounts');
    } else {
      let portfolioData = {
        ...formData,
      };

      if (valueMode === 'detailed') {
        portfolioData.currentValue = 0;
        portfolioData.costBasis = 0;
      } else {
        portfolioData.currentValue = formData.currentValue || 0;
        portfolioData.costBasis = formData.costBasis || 0;
      }

      const newPortfolio = addPortfolio(portfolioData);

      if (newPortfolio) {
        // Create a manual transaction for the initial value in simple mode
        if (valueMode === 'simple' && (formData.currentValue || 0) > 0) {
          addTransaction({
            portfolioId: newPortfolio.id,
            type: 'manual',
            amount: formData.currentValue,
            date: newPortfolio.createdAt,
            description: `Initial account value: ${formData.currentValue.toFixed(2)}`,
          });
        }

        if (valueMode === 'detailed' && pots.length > 0) {
          pots.forEach(pot => {
            const potData = {
              portfolioId: newPortfolio.id,
              name: pot.name,
              type: pot.type,
              currency: pot.currency,
            };

            if (pot.type === 'cash') {
              potData.amount = pot.amount;
              potData.currentValue = pot.amount;
            } else {
              potData.fundId = pot.fundId || '';
              potData.symbol = pot.symbol || '';
              potData.units = pot.units;
              potData.pricePerUnit = pot.pricePerUnit;
              potData.costBasis = pot.costBasis;
              potData.currentValue = pot.units * pot.pricePerUnit;
              potData.lowReturn = pot.lowReturn;
              potData.averageReturn = pot.averageReturn;
              potData.highReturn = pot.highReturn;
            }

            addPot(potData, false);
          });
        }
      }

      if (keepOpen) {
        setFormData({
          name: '',
          type: 'ISA',
          currency: 'GBP',
          currentValue: 0,
          costBasis: 0,
          monthlyContribution: 0,
          contributionDay: '',
          contributionSplits: [],
          lowReturn: 3,
          averageReturn: 7,
          highReturn: 10,
        });
        setValueMode('simple');
        setPots([]);
        setErrors({});
      } else {
        navigate('/accounts');
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/accounts')}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          ← Back to Accounts
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mt-2">
          {account ? 'Edit Account' : 'Add Account'}
        </h1>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-6">
          {/* Account Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Account Name <span className="text-red-600">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., IG Smart Portfolio ISA"
              className={`block w-full max-w-md px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Account Type and Currency - Grouped */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Account Details</h3>
            <div className="flex gap-4">
              <div className="w-64">
                <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                  Account Type <span className="text-red-600">*</span>
                </label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className={`block w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.type ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  {accountTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                {errors.type && (
                  <p className="mt-1 text-sm text-red-600">{errors.type}</p>
                )}
              </div>

              <div className="w-48">
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                  Currency <span className="text-red-600">*</span>
                </label>
                <select
                  id="currency"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className={`block w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors.currency ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  {currencies.map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.name}
                    </option>
                  ))}
                </select>
                {errors.currency && (
                  <p className="mt-1 text-sm text-red-600">{errors.currency}</p>
                )}
              </div>
            </div>
          </div>

          {/* Value Mode Selector - Only show for new accounts */}
          {!account && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How would you like to set the account value? <span className="text-red-600">*</span>
              </label>
              <div className="flex gap-3 max-w-lg">
                <button
                  type="button"
                  onClick={() => setValueMode('simple')}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    valueMode === 'simple'
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={valueMode === 'simple' ? { backgroundColor: '#012E58' } : {}}
                >
                  Simple
                </button>
                <button
                  type="button"
                  onClick={() => setValueMode('detailed')}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    valueMode === 'detailed'
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  style={valueMode === 'detailed' ? { backgroundColor: '#012E58' } : {}}
                >
                  Detailed (Add Pots/Funds)
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {valueMode === 'simple'
                  ? 'Enter a total value for the account'
                  : 'Define the breakdown of cash pots and stock/fund holdings'}
              </p>
            </div>
          )}

          {/* Simple Mode - Value Fields - Grouped */}
          {valueMode === 'simple' && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Account Values</h3>
              <div className="flex gap-4 flex-wrap">
                {/* Current Value */}
                <div style={{ width: '150px' }}>
                  <label htmlFor="currentValue" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Value <span className="text-red-600">*</span>
                  </label>
                  <input
                    id="currentValue"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.currentValue}
                    onChange={(e) => setFormData({ ...formData, currentValue: parseFloat(e.target.value) || 0 })}
                    className={`block w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.currentValue ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.currentValue && (
                    <p className="mt-1 text-xs text-red-600">{errors.currentValue}</p>
                  )}
                </div>

                {/* Cost Basis */}
                <div style={{ width: '150px' }}>
                  <label htmlFor="costBasis" className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                    <span>Cost Basis <span className="text-red-600">*</span></span>
                    <Tooltip text="The total amount you've invested in this account, excluding any gains or losses." position="top" />
                  </label>
                  <input
                    id="costBasis"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.costBasis}
                    onChange={(e) => setFormData({ ...formData, costBasis: parseFloat(e.target.value) || 0 })}
                    className={`block w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.costBasis ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.costBasis && (
                    <p className="mt-1 text-xs text-red-600">{errors.costBasis}</p>
                  )}
                </div>

                {/* Monthly Contribution */}
                <div style={{ width: '150px' }}>
                  <label htmlFor="monthlyContribution-simple" className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                    <span>Monthly Contrib.</span>
                    <Tooltip text="How much you contribute to this account each month." position="top" />
                  </label>
                  <input
                    id="monthlyContribution-simple"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.monthlyContribution}
                    onChange={(e) => setFormData({ ...formData, monthlyContribution: parseFloat(e.target.value) || 0 })}
                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>

                {/* Contribution Day */}
                <div style={{ width: '120px' }}>
                  <label htmlFor="contributionDay-simple" className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                    <span>Day of Month</span>
                    <Tooltip text="The day each month when your contribution is deposited. A proposed transaction will be created on this day for you to confirm." position="top" />
                  </label>
                  <input
                    id="contributionDay-simple"
                    type="number"
                    min="1"
                    max="31"
                    value={formData.contributionDay}
                    onChange={(e) => setFormData({ ...formData, contributionDay: parseInt(e.target.value) || '' })}
                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. 15"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Detailed Mode - Pot Builder */}
          {valueMode === 'detailed' && !account && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-900">Pots & Funds</h4>
                <button
                  type="button"
                  onClick={addNewPot}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: '#012E58' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
                >
                  <PlusIcon className="w-4 h-4" />
                  Add Pot
                </button>
              </div>

              {errors.pots && (
                <p className="text-sm text-red-600 mb-2">{errors.pots}</p>
              )}

              {pots.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No pots added yet. Click "Add Pot" to get started.
                </p>
              ) : (
                <div className="space-y-3">
                  {pots.map((pot, index) => (
                    <div key={pot.id} className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-700">Pot {index + 1}</span>
                        <button
                          type="button"
                          onClick={() => removePot(pot.id)}
                          className="text-red-600 hover:text-red-700 p-1"
                          title="Remove pot"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Pot Type Toggle */}
                      <div className="flex gap-2 mb-2">
                        <button
                          type="button"
                          onClick={() => updatePotField(pot.id, 'type', 'cash')}
                          className={`flex-1 px-2 py-1 text-xs font-medium rounded ${
                            pot.type === 'cash'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          Cash
                        </button>
                        <button
                          type="button"
                          onClick={() => updatePotField(pot.id, 'type', 'stock')}
                          className={`flex-1 px-2 py-1 text-xs font-medium rounded ${
                            pot.type === 'stock'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          Stock/Fund
                        </button>
                      </div>

                      {/* Pot Name */}
                      <input
                        type="text"
                        placeholder={pot.type === 'cash' ? 'e.g., Emergency Fund' : 'e.g., Vanguard FTSE All-World'}
                        value={pot.name}
                        onChange={(e) => updatePotField(pot.id, 'name', e.target.value)}
                        className={`w-full px-2 py-1.5 text-sm border rounded mb-2 ${
                          errors[`pot_${pot.id}_name`] ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors[`pot_${pot.id}_name`] && (
                        <p className="text-xs text-red-600 mb-1">{errors[`pot_${pot.id}_name`]}</p>
                      )}

                      {/* Cash Pot Fields */}
                      {pot.type === 'cash' && (
                        <input
                          type="number"
                          placeholder="Amount"
                          step="0.01"
                          min="0"
                          value={pot.amount || ''}
                          onChange={(e) => updatePotField(pot.id, 'amount', parseFloat(e.target.value) || 0)}
                          className={`w-full px-2 py-1.5 text-sm border rounded ${
                            errors[`pot_${pot.id}_amount`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                      )}
                      {errors[`pot_${pot.id}_amount`] && (
                        <p className="text-xs text-red-600 mt-1">{errors[`pot_${pot.id}_amount`]}</p>
                      )}

                      {/* Stock/Fund Fields */}
                      {pot.type === 'stock' && (
                        <div className="space-y-2">
                          {/* Fund Selector */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Select Fund
                            </label>
                            <div className="flex gap-2">
                              <select
                                value={pot.fundId || ''}
                                onChange={(e) => updatePotField(pot.id, 'fundId', e.target.value)}
                                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded"
                              >
                                <option value="">-- Manual Entry --</option>
                                {[...fundPrices]
                                  .sort((a, b) => a.name.localeCompare(b.name))
                                  .map(fund => (
                                    <option key={fund.id} value={fund.id}>
                                      {fund.name} {fund.symbol ? `(${fund.symbol})` : ''} - {formatCurrency(fund.price, fund.currency, 2)}
                                    </option>
                                  ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => handleOpenFundModal(pot.id)}
                                className="flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
                                style={{ backgroundColor: '#059669' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#047857'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#059669'}
                                title="Add new fund"
                              >
                                <PlusIcon className="w-3 h-3" />
                              </button>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                              {pot.fundId ? 'Using fund from library' : 'Manually enter details below'}
                            </p>
                          </div>

                          {/* Symbol - show for reference or manual entry */}
                          <input
                            type="text"
                            placeholder="Symbol (optional)"
                            value={pot.symbol || ''}
                            onChange={(e) => updatePotField(pot.id, 'symbol', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                            disabled={!!pot.fundId}
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <input
                                type="number"
                                placeholder="Units"
                                step="0.0001"
                                min="0"
                                value={pot.units || ''}
                                onChange={(e) => updatePotField(pot.id, 'units', parseFloat(e.target.value) || 0)}
                                className={`w-full px-2 py-1.5 text-sm border rounded ${
                                  errors[`pot_${pot.id}_units`] ? 'border-red-500' : 'border-gray-300'
                                }`}
                              />
                              {errors[`pot_${pot.id}_units`] && (
                                <p className="text-xs text-red-600 mt-1">{errors[`pot_${pot.id}_units`]}</p>
                              )}
                            </div>
                            <div>
                              <input
                                type="number"
                                placeholder="Price/Unit"
                                step="0.01"
                                min="0"
                                value={pot.pricePerUnit || ''}
                                onChange={(e) => updatePotField(pot.id, 'pricePerUnit', parseFloat(e.target.value) || 0)}
                                className={`w-full px-2 py-1.5 text-sm border rounded ${
                                  errors[`pot_${pot.id}_price`] ? 'border-red-500' : 'border-gray-300'
                                }`}
                                disabled={!!pot.fundId}
                              />
                              {errors[`pot_${pot.id}_price`] && (
                                <p className="text-xs text-red-600 mt-1">{errors[`pot_${pot.id}_price`]}</p>
                              )}
                              {pot.fundId && (
                                <p className="text-xs text-gray-500 mt-1">Auto-filled from fund</p>
                              )}
                            </div>
                          </div>
                          <input
                            type="number"
                            placeholder="Cost Basis"
                            step="0.01"
                            min="0"
                            value={pot.costBasis || ''}
                            onChange={(e) => updatePotField(pot.id, 'costBasis', parseFloat(e.target.value) || 0)}
                            className={`w-full px-2 py-1.5 text-sm border rounded ${
                              errors[`pot_${pot.id}_costBasis`] ? 'border-red-500' : 'border-gray-300'
                            }`}
                          />
                          {errors[`pot_${pot.id}_costBasis`] && (
                            <p className="text-xs text-red-600 mt-1">{errors[`pot_${pot.id}_costBasis`]}</p>
                          )}
                          {pot.units > 0 && pot.pricePerUnit > 0 && (
                            <p className="text-xs text-gray-600">
                              Current Value: {formatCurrency(pot.units * pot.pricePerUnit, formData.currency, 2)}
                            </p>
                          )}

                          {/* Return Rates for Stock/Fund */}
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs font-medium text-gray-700 mb-1">Annual Return Rates (%)</p>
                            <div className="grid grid-cols-3 gap-2">
                              <input
                                type="number"
                                placeholder="Low"
                                step="0.01"
                                min="0"
                                max="100"
                                value={pot.lowReturn || 0}
                                onChange={(e) => updatePotField(pot.id, 'lowReturn', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                              />
                              <input
                                type="number"
                                placeholder="Avg"
                                step="0.01"
                                min="0"
                                max="100"
                                value={pot.averageReturn || 0}
                                onChange={(e) => updatePotField(pot.id, 'averageReturn', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                              />
                              <input
                                type="number"
                                placeholder="High"
                                step="0.01"
                                min="0"
                                max="100"
                                value={pot.highReturn || 0}
                                onChange={(e) => updatePotField(pot.id, 'highReturn', parseFloat(e.target.value) || 0)}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                              />
                            </div>
                            {(errors[`pot_${pot.id}_lowReturn`] || errors[`pot_${pot.id}_avgReturn`]) && (
                              <p className="text-xs text-red-600 mt-1">
                                {errors[`pot_${pot.id}_lowReturn`] || errors[`pot_${pot.id}_avgReturn`]}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Return Rates for Cash Pots */}
                      {pot.type === 'cash' && (
                        <div className="mt-2 pt-2 border-t border-gray-200">
                          <p className="text-xs font-medium text-gray-700 mb-1">Annual Return Rates (%) - Optional for cash</p>
                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="number"
                              placeholder="Low"
                              step="0.01"
                              min="0"
                              max="100"
                              value={pot.lowReturn || 0}
                              onChange={(e) => updatePotField(pot.id, 'lowReturn', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                            />
                            <input
                              type="number"
                              placeholder="Avg"
                              step="0.01"
                              min="0"
                              max="100"
                              value={pot.averageReturn || 0}
                              onChange={(e) => updatePotField(pot.id, 'averageReturn', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                            />
                            <input
                              type="number"
                              placeholder="High"
                              step="0.01"
                              min="0"
                              max="100"
                              value={pot.highReturn || 0}
                              onChange={(e) => updatePotField(pot.id, 'highReturn', parseFloat(e.target.value) || 0)}
                              className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
                            />
                          </div>
                          {(errors[`pot_${pot.id}_lowReturn`] || errors[`pot_${pot.id}_avgReturn`]) && (
                            <p className="text-xs text-red-600 mt-1">
                              {errors[`pot_${pot.id}_lowReturn`] || errors[`pot_${pot.id}_avgReturn`]}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Total Summary */}
              {pots.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-300">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700">Total Current Value:</span>
                    <span className="font-bold text-gray-900">
                      {formatCurrency(calculateTotalFromPots(), formData.currency, 2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm mt-1">
                    <span className="font-medium text-gray-700">Total Cost Basis:</span>
                    <span className="font-bold text-gray-900">
                      {formatCurrency(calculateCostBasisFromPots(), formData.currency, 2)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Monthly Contribution - Only for Detailed Mode */}
          {valueMode === 'detailed' && !account && (
            <div className="flex gap-4">
              <div style={{ width: '150px' }}>
                <label htmlFor="monthlyContribution" className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                  <span>Monthly Contrib.</span>
                  <Tooltip text="Total monthly amount added to this account. The system will distribute it proportionally based on your pot allocations." position="top" />
                </label>
                <input
                  id="monthlyContribution"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.monthlyContribution}
                  onChange={(e) => setFormData({ ...formData, monthlyContribution: parseFloat(e.target.value) || 0 })}
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                />
              </div>
              <div style={{ width: '120px' }}>
                <label htmlFor="contributionDay-detailed" className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                  <span>Day of Month</span>
                  <Tooltip text="The day each month when your contribution is deposited." position="top" />
                </label>
                <input
                  id="contributionDay-detailed"
                  type="number"
                  min="1"
                  max="31"
                  value={formData.contributionDay}
                  onChange={(e) => setFormData({ ...formData, contributionDay: parseInt(e.target.value) || '' })}
                  className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. 15"
                />
              </div>
            </div>
          )}

          {/* Estimated Return Rates - Only show in Simple mode or when editing - Grouped */}
          {(valueMode === 'simple' || account) && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Expected Annual Return Rates <span className="text-red-600">*</span>
              </h3>
              <div className="flex gap-4 flex-wrap">
                {/* Low Return */}
                <div style={{ width: '90px' }}>
                  <label htmlFor="lowReturn" className="flex items-center gap-1 text-xs font-medium text-gray-700 mb-1">
                    <span>Low (%)</span>
                    <Tooltip text="Pessimistic return rate" position="top" />
                  </label>
                  <input
                    id="lowReturn"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.lowReturn}
                    onChange={(e) => setFormData({ ...formData, lowReturn: parseFloat(e.target.value) || 0 })}
                    className={`block w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.lowReturn ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="3.0"
                  />
                  {errors.lowReturn && (
                    <p className="mt-1 text-xs text-red-600">{errors.lowReturn}</p>
                  )}
                </div>

                {/* Average Return */}
                <div style={{ width: '90px' }}>
                  <label htmlFor="averageReturn" className="flex items-center gap-1 text-xs font-medium text-gray-700 mb-1">
                    <span>Average (%)</span>
                    <Tooltip text="Expected return rate" position="top" />
                  </label>
                  <input
                    id="averageReturn"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.averageReturn}
                    onChange={(e) => setFormData({ ...formData, averageReturn: parseFloat(e.target.value) || 0 })}
                    className={`block w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.averageReturn ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="7.0"
                  />
                  {errors.averageReturn && (
                    <p className="mt-1 text-xs text-red-600">{errors.averageReturn}</p>
                  )}
                </div>

                {/* High Return */}
                <div style={{ width: '90px' }}>
                  <label htmlFor="highReturn" className="flex items-center gap-1 text-xs font-medium text-gray-700 mb-1">
                    <span>High (%)</span>
                    <Tooltip text="Optimistic return rate" position="top" />
                  </label>
                  <input
                    id="highReturn"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.highReturn}
                    onChange={(e) => setFormData({ ...formData, highReturn: parseFloat(e.target.value) || 0 })}
                    className={`block w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.highReturn ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="10.0"
                  />
                  {errors.highReturn && (
                    <p className="mt-1 text-xs text-red-600">{errors.highReturn}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Detailed Mode - Info about return rates */}
          {valueMode === 'detailed' && !account && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 max-w-2xl">
              <p className="text-xs text-blue-800 m-0">
                <strong>Return rates:</strong> Set individual return rates for each pot/fund above. The account's overall return will be calculated as a weighted average based on the allocation.
              </p>
            </div>
          )}

          {/* Monthly Contribution Settings - Show when editing an existing account */}
          {account && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Monthly Contribution Settings
              </h3>
              <div className="flex gap-4 flex-wrap mb-4">
                <div style={{ width: '150px' }}>
                  <label htmlFor="monthlyContribution-edit" className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                    <span>Amount</span>
                    <Tooltip text="How much you contribute to this account each month." position="top" />
                  </label>
                  <input
                    id="monthlyContribution-edit"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.monthlyContribution}
                    onChange={(e) => setFormData({ ...formData, monthlyContribution: parseFloat(e.target.value) || 0 })}
                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                </div>
                <div style={{ width: '120px' }}>
                  <label htmlFor="contributionDay-edit" className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-1">
                    <span>Day of Month</span>
                    <Tooltip text="The day each month when your contribution is deposited. A proposed transaction will be created on this day for you to confirm." position="top" />
                  </label>
                  <input
                    id="contributionDay-edit"
                    type="number"
                    min="1"
                    max="31"
                    value={formData.contributionDay}
                    onChange={(e) => setFormData({ ...formData, contributionDay: parseInt(e.target.value) || '' })}
                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. 15"
                  />
                </div>
              </div>

              {/* Default Pot Splits */}
              {(() => {
                const accountPots = allPots.filter(p => p.portfolioId === account.id);
                if (accountPots.length === 0) return null;

                const splits = formData.contributionSplits || [];
                const totalAllocated = splits.reduce((sum, s) => sum + (s.percentage || 0), 0);

                return (
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-2">
                      <span>Default Pot Splits</span>
                      <Tooltip text="How the monthly contribution should be split across your pots. You can override this when confirming each contribution." position="top" />
                    </label>
                    <div className="space-y-2">
                      {splits.map((split, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <select
                            value={split.potId}
                            onChange={(e) => {
                              const updated = [...splits];
                              updated[idx] = { ...updated[idx], potId: e.target.value };
                              setFormData({ ...formData, contributionSplits: updated });
                            }}
                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select pot...</option>
                            {accountPots.map(pot => (
                              <option key={pot.id} value={pot.id}>{pot.name}</option>
                            ))}
                          </select>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={split.percentage}
                            onChange={(e) => {
                              const updated = [...splits];
                              updated[idx] = { ...updated[idx], percentage: parseFloat(e.target.value) || 0 };
                              setFormData({ ...formData, contributionSplits: updated });
                            }}
                            className="w-20 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="%"
                          />
                          <span className="text-xs text-gray-500">%</span>
                          <button
                            type="button"
                            onClick={() => {
                              const updated = splits.filter((_, i) => i !== idx);
                              setFormData({ ...formData, contributionSplits: updated });
                            }}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-3 mt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            contributionSplits: [...splits, { potId: '', percentage: 0 }],
                          });
                        }}
                        className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
                      >
                        <PlusIcon className="w-3 h-3" />
                        Add Split
                      </button>
                      {totalAllocated > 0 && (
                        <span className={`text-xs ${totalAllocated === 100 ? 'text-green-600' : 'text-amber-600'}`}>
                          {totalAllocated}% allocated{totalAllocated !== 100 && ` (${100 - totalAllocated}% unallocated → General pot)`}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Goal Allocations for this contribution */}
              {account && (() => {
                const accountGoals = goals.filter(g =>
                  g.allocations?.some(a => a.portfolioId === account.id)
                );
                const unlinkedGoals = goals.filter(g =>
                  !g.allocations?.some(a => a.portfolioId === account.id)
                );

                const toggleGoalAllocation = (goal, addIt) => {
                  const currentAllocations = goal.allocations || [];
                  if (addIt) {
                    updateGoal(goal.id, {
                      allocations: [
                        ...currentAllocations,
                        { portfolioId: account.id, type: 'percentage', value: 100 },
                      ],
                    });
                  } else {
                    updateGoal(goal.id, {
                      allocations: currentAllocations.filter(a => a.portfolioId !== account.id),
                    });
                  }
                };

                const updateAllocationPct = (goal, value) => {
                  updateGoal(goal.id, {
                    allocations: (goal.allocations || []).map(a =>
                      a.portfolioId === account.id ? { ...a, value: parseFloat(value) || 0 } : a
                    ),
                  });
                };

                return (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <label className="flex items-center gap-1 text-sm font-medium text-gray-700 mb-2">
                      <span>Goal Allocations</span>
                      <Tooltip text="Which goals this account's contributions count toward, and what percentage of the account value is attributed to each goal." position="top" />
                    </label>

                    {accountGoals.length === 0 && (
                      <p className="text-xs text-gray-400 mb-2">No goals linked to this account yet.</p>
                    )}

                    <div className="space-y-2">
                      {accountGoals.map(goal => {
                        const alloc = goal.allocations.find(a => a.portfolioId === account.id);
                        const pct = alloc?.type === 'percentage' ? alloc.value ?? 100 : null;
                        return (
                          <div key={goal.id} className="flex items-center gap-2">
                            <span className="flex-1 text-sm text-gray-800 truncate">{goal.name}</span>
                            {pct !== null && (
                              <>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={pct}
                                  onChange={(e) => updateAllocationPct(goal, e.target.value)}
                                  className="w-20 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                <span className="text-xs text-gray-500">%</span>
                              </>
                            )}
                            {pct === null && (
                              <span className="text-xs text-gray-400 italic">fixed amount</span>
                            )}
                            <button
                              type="button"
                              onClick={() => toggleGoalAllocation(goal, false)}
                              className="text-red-500 hover:text-red-700 p-1"
                              title="Remove from goal"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>

                    {unlinkedGoals.length > 0 && (
                      <div className="mt-2">
                        <select
                          defaultValue=""
                          onChange={(e) => {
                            if (!e.target.value) return;
                            const goal = goals.find(g => g.id === e.target.value);
                            if (goal) toggleGoalAllocation(goal, true);
                            e.target.value = '';
                          }}
                          className="w-full px-3 py-1.5 text-sm border border-dashed border-gray-300 rounded-lg text-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">+ Link to a goal…</option>
                          {unlinkedGoals.map(g => (
                            <option key={g.id} value={g.id}>{g.name}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/accounts')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            {!account && (
              <button
                type="button"
                onClick={() => handleSubmit(true)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Add Another Account
              </button>
            )}
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
              style={{ backgroundColor: '#012E58' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#01234a'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#012E58'}
            >
              {account ? 'Update Account' : 'Add Account'}
            </button>
          </div>
        </form>
      </div>

      {/* Fund Price Modal - for adding new funds inline */}
      <FundPriceModal
        visible={isFundModalVisible}
        onCancel={handleCloseFundModal}
        fund={null}
        onFundAdded={handleFundAdded}
      />
    </div>
  );
};

export default AccountForm;
